import React from "react";
import HocPopup from "../../../../hoc/hocPopup";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  TextField,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  TablePagination,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EventIcon from "@mui/icons-material/Event";
import moment from "moment";
import { useState } from "react";
import { ErrorOutlineOutlined } from "@mui/icons-material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useEffect } from "react";

import { useAuth } from "../../../../context/Auth.context";
import { TYPE, formatDate, formatDateWithTime } from "../../../../utils/Utils";
import CustomerInfoDialog from "./dialog/dialogbody/CustomerInfo.dialog";
import PullDetailDialog from "./dialog/dialogbody/PullDetail.dialog";
import { TransactionHistoryStyle } from "./TransactionHistory.style";
import VerificationProcessDialog from "./dialog/dialogbody/VerificationProcess.dialog";
import BreadcrumbsComponent from "../../../base/Breadcrumbs.component";
import { useMutation, useSubscription } from "@apollo/client";
import { TRANSACTION_HISTORY } from "../../../../graphql/mutation/mutation";
import { useAppContext } from "../../../../context/App.context";
import { ON_LOG_ADDED } from "../../../../graphql/subscription/subscription";
import { InputValidation } from "../../../../utils/InputValidationRegex";

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const INITIAL_FILTER = {
  customerName: "",
  dealershipName: "",
  date: moment(
    new Date().toLocaleString("en-US", {
      timeZone: userTimezone,
    })
  ).format("YYYY-MM-DD"),
  timestamp: new Date().getTimezoneOffset(),
  // pageCount: 0,
  // rowsPerPage: 10,
};
const breadcrumb = {
  title: "Transaction History",
  links: [
    { path: "/", name: "Dealership" },
    { path: null, name: "Transactions" },
  ],
};
const TransactionsComponent = ({ dialog }) => {
  const auth = useAuth();
  const app = useAppContext();
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(1000);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [transactions, setTransactions] = useState([]);

  const [filterData, setFilterData] = useState(INITIAL_FILTER);
  const [
    fetchTransactions,
    { loading: updateLoading, data: fetchingTransactions },
  ] = useMutation(TRANSACTION_HISTORY);
  const { data: onFetchTransaction } = useSubscription(ON_LOG_ADDED, {
    fetchPolicy: "no-cash",
    variables: {
      date: filterData.date,
    },
  });

  useEffect(() => {
    fetchTransactions({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
      variables: {
        transactionInput: {
          date: filterData.date,
          search_customer: filterData.customerName,
          search_dealership: filterData.dealershipName,
          timestamp: filterData.timestamp,
          rowsPerPage: rowsPerPage,
          pageCount: page,
          timezone: userTimezone,
        },
      },
    });
  }, [filterData, page, rowsPerPage]);

  useEffect(() => {
    if (fetchingTransactions?.fetchTransactions?.transaction) {
      setTransactions(fetchingTransactions?.fetchTransactions?.transaction);
      setCount(fetchingTransactions?.fetchTransactions?.total);
    }
  }, [fetchingTransactions]);

  useEffect(() => {
    if (onFetchTransaction?.onLogAdded?.transaction) {
      setTransactions(onFetchTransaction.onLogAdded?.transaction);
      setCount(fetchingTransactions?.fetchTransactions?.total);
    }
  }, [onFetchTransaction]);

  return (
    <>
      <BreadcrumbsComponent {...breadcrumb} />
      <Paper sx={TransactionHistoryStyle.papers}>
        <Box sx={TransactionHistoryStyle.noValidate}>
          <FormControl
            sx={TransactionHistoryStyle.filter}
            variant="outlined"
            size="small"
          >
            <InputLabel>Customer Name</InputLabel>
            <OutlinedInput
              label="Customer Name"
              value={filterData.customerName}
              type="text"
              onChange={(event) => {
                setFilterData({
                  ...filterData,
                  customerName: event.target.value,
                });
              }}
            />
          </FormControl>
          <FormControl
            sx={TransactionHistoryStyle.filter}
            variant="outlined"
            size="small"
          >
            <InputLabel>Dealership Name</InputLabel>
            <OutlinedInput
              label="Dealership Name"
              value={filterData.dealershipName}
              type="text"
              onChange={(event) => {
                setFilterData({
                  ...filterData,
                  dealershipName: event.target.value,
                });
              }}
            />
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={formatDate(filterData.date)}
              maxDate={new Date()}
              onChange={(event) => {
                setFilterData({
                  ...filterData,
                  date: moment(event).format("YYYY-MM-DD"),
                });
              }}
              renderInput={(params) => (
                <TextField
                  size="small"
                  sx={TransactionHistoryStyle.filter}
                  {...params}
                />
              )}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <EventIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </LocalizationProvider>
        </Box>
        <TableContainer
          sx={{
            maxHeight:
              window.innerHeight >= 1440
                ? "82vh"
                : window.innerHeight >= 1050
                ? "78vh"
                : window.innerHeight >= 900
                ? "67vh"
                : "63vh",
            height:
              window.innerHeight >= 1440
                ? "82vh"
                : window.innerHeight >= 1050
                ? "78vh"
                : window.innerHeight >= 900
                ? "67vh"
                : "63vh",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Time
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Dealership
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Employee
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Customer
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Email
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Contact Number
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Vehicle
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Status
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Pull ID
                </TableCell>
                <TableCell sx={TransactionHistoryStyle.tableCell}>
                  Payload
                </TableCell>
              </TableRow>
            </TableHead>
            {transactions.length > 0 ? (
              <TableBody>
                {transactions.map((history, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.transactionCell}
                      >
                        {formatDateWithTime(history.timestamp).substring(0, 10)}
                        <br />
                        {formatDateWithTime(history.timestamp).substring(10)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.transactionCell}
                      >
                        {history.dealershipName || "N/A"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.transactionCell}
                      >
                        {history.employeeName}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.info}
                        onClick={() => {
                          dialog({
                            open: true,
                            bodyType: TYPE.CUSTOMER_INFO,
                            maxWidth: "sm",
                            body: (
                              <CustomerInfoDialog customer={history.customer} />
                            ),
                          });
                        }}
                      >
                        {history.customer?.fullName}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.transactionCell}
                      >
                        {history.customer?.email || "N/A"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.transactionCell}
                      >
                        {history.customer?.contactNumber
                          ? InputValidation.phoneNumberFormat(
                              history.customer?.contactNumber
                            )
                          : "N/A"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.transactionCell}
                      >
                        {history.customer?.vin || "N/A"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.transactionCell}
                      >
                        {history?.status?.pull ? "COMPLETED" : "INCOMPLETE"}
                      </TableCell>
                      {history?.pull?.pullId ? (
                        <TableCell
                          align="center"
                          sx={TransactionHistoryStyle.info}
                          onClick={() => {
                            dialog({
                              open: true,
                              bodyType: TYPE.PULL_DETAILS,
                              maxWidth: "sm",
                              body: <PullDetailDialog pull={history.pull} />,
                            });
                          }}
                        >
                          {history?.pull?.pullId}
                        </TableCell>
                      ) : (
                        <TableCell
                          align="center"
                          sx={TransactionHistoryStyle.transactionCell}
                        >
                          N/A
                        </TableCell>
                      )}

                      <TableCell
                        align="center"
                        sx={TransactionHistoryStyle.transactionCell}
                      >
                        {history?.status?.pull ? (
                          <InsertDriveFileOutlinedIcon
                            color={
                              // !checkPolicies(history?.payload)
                              !history?.is_issue ? "primary" : "error"
                            }
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              let output = JSON.parse(history?.output_payload);
                              dialog({
                                open: true,
                                bodyType: TYPE.OUTPUT_PAYLOAD,
                                maxWidth: "lg",
                                body: (
                                  <VerificationProcessDialog
                                    payload={output?.pull}
                                  />
                                ),
                              });
                            }}
                          />
                        ) : (
                          <ErrorOutlineOutlined
                            color="error"
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              dialog({
                                open: true,
                                bodyType: TYPE.CANOPY_ERROR,
                                maxWidth: "md",
                                body: (
                                  <VerificationProcessDialog
                                    pull={history?.pull}
                                    status={history?.status}
                                  />
                                ),
                              });
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            ) : (
              <TableRow>
                <TableCell
                  sx={{
                    border: "none",
                    padding: "0px 16px 0px 16px",
                    textAlign: "center",
                  }}
                  colSpan={11}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                      margin: "16px",
                    }}
                  >
                    <h2>
                      {updateLoading ? "Loading..." : "No data to display"}
                    </h2>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => {
            setPage(newPage);
          }}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 5));
            setPage(0);
          }}
        />
      </Paper>
    </>
  );
};

export default HocPopup(TransactionsComponent);
